import { Table } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { sendSingleRequest } from '../../apis';
import {
  BreadcrumbComponent,
  Button,
  ModalComponent,
  Paragrapgh,
  Spinner,
  Subtitle,
  Title,
} from '../../components/shared';
import {
  IAssessmentResource,
  IFarmProperty,
  ILineProperty,
  ILineResource,
  IMaintenanceResource,
} from '../../entities/farms.entities';
import getBreadcrumbMenu from '../../lib/BreadcrumbMenu';
import { showFeedback } from '../../store/ui/ui.actions';
import HarvestsTable from '../../components/lines/HarvestsTable';
import TimelineView from '../../components/lines/TimelineView';
import {
  IHarvestResource,
  IMusselHarvest,
  IMusselSeeding,
  IOysterHarvest,
  IOysterSeeding,
  ISeaweedHarvest,
  ISeaweedSeeding,
  ISeedingResource,
} from '../../entities/growing.entities';
import MaintenanceTable from '../../components/lines/MaintenanceTable';
import MusselHarvestHeader from './MusselHarvestHeader';
import OysterHarvestHeader from './OysterHarvestHeader';
import SeaweedHarvestHeader from './SeaweedHarvestHeader';
import { TBusinessType } from '../../entities/general.entities';
import HarvestTrackModal from '../../components/track-modals/HarvestTrackModal';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import { TLang } from '../../entities/ui.entities';
import { selectAllFarms } from '../../store/extra/extra.selector';
import { defaultDateFormat, displayValueUnit } from '../../lib/common.helpers';
import { IInventoryManage } from '../../entities/inventory.entities';
import FilesViewModal from '../../components/shared/pictures-wall/FilesViewModal';
import { selectAccount } from '../../store/auth/auth.selector';
import { SeedSizeRange } from '../../components/CommonViews';

interface IDataResource {
  crop_type: TBusinessType;
  farm: IFarmProperty;
  line: ILineProperty;
  seedings: ISeedingResource[];
  maintenances: IMaintenanceResource[];
  assessments: IAssessmentResource[];
  inventory_seeds: IInventoryManage[];
  harvests: IHarvestResource[];
  pdf_form?: any;
  main_seeding_id: number;
  main_harvest_id: number;
  first_stage_length: number | null;
  prev_stage_length: number | null;
  last_stage_length: number | null;
  stage_length_sum: number;
  stage_amount_sum: number;
  stage_mortality_sum: number;
  grown_days: number;
  has_tracks: boolean;
  combined_lines: IHarvestResource['lines'];
}

const seedColumns = (
  type: TBusinessType,
  unitType: any,
  method: string | null,
  lang: TLang | undefined,
) => {
  let result: any = [
    {
      key: 'id',
      dataIndex: 'id',
      title: 'ID',
      render: (x: any) => <span>{x}</span>,
    },
    {
      key: 'planned_date_seed',
      dataIndex: 'planned_date_seed',
      title: translate(lang, 'Date seeded'),
      render: (x: any) => <span>{defaultDateFormat(x)}</span>,
    },
    {
      key: 'planned_date_harvest',
      dataIndex: 'planned_date_harvest',
      title: translate(lang, 'Planned date harvest'),
      render: (x: any) => <span>{defaultDateFormat(x)}</span>,
    },
    {
      key: 'season_name',
      dataIndex: 'season_name',
      title: translate(lang, 'Season'),
      render: (x: string) => <span>{x}</span>,
    },
  ];
  if (type === 'MUSSEL') {
    result.push(
      {
        key: 'line_length',
        title: translate(lang, 'Longline length'),
        render: (x: IMusselSeeding) => (
          <span>
            {displayValueUnit(x.line_length, unitType, 'distance_m', '-')}
          </span>
        ),
      },
      {
        key: 'spat_storage',
        dataIndex: '',
        title: translate(lang, 'Spats'),
        render: (x: IMusselSeeding) => (
          <span>
            {x.spat_storage
              ? `${x.spat_storage.source} - ${displayValueUnit(
                  x.spat_amount,
                  unitType,
                  'weight_kg',
                )}`
              : ''}
          </span>
        ),
      },
      {
        key: 'drop',
        dataIndex: 'drop',
        title: translate(lang, 'Drop'),
        render: (x: any) => <span>{x === null ? '-' : x}</span>,
      },
      {
        key: 'submersion',
        dataIndex: 'submersion',
        title: translate(lang, 'Submersion'),
        render: (x: any) => <span>{x === null ? '-' : x}</span>,
      },
    );
  } else if (type === 'OYSTER') {
    result.push(
      {
        key: 'basket_count',
        title: translate(lang, '%s count', method),
        render: (x: IOysterSeeding) => <span>{x.basket_count ?? ''}</span>,
      },
      {
        key: 'spat_storage',
        dataIndex: '',
        title: translate(lang, 'Spats'),
        render: (x: IOysterSeeding) => (
          <span>
            {x.spat_storage
              ? `${x.spat_storage.source} - ${x.spat_amount_dz} dz`
              : ''}
          </span>
        ),
      },
    );
  }
  result.push(
    {
      key: 'spat_size',
      dataIndex: '',
      title: translate(lang, 'Spat Size'),
      render: (x: ISeedingResource) => <SeedSizeRange data={x} />,
    },
    {
      key: 'density',
      title: translate(lang, 'Density'),
      render: (x: any) => <SeedSizeRange data={x} />,
    },
  );
  return result;
};

const formDownload = (pdfFile: any) => {
  if (!pdfFile) return;
  const bc = atob(pdfFile);
  const bn = bc.split('').map(x => x.charCodeAt(0));
  const ba = new Uint8Array(bn);
  const blob = new Blob([ba], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'harvest-form.pdf';
  link.click();
  window.URL.revokeObjectURL(link.href);
};

const HarvestDetailPage = () => {
  const dispatch = useDispatch();
  const lang = useSelector(selectLang);
  const farmsData = useSelector(selectAllFarms);
  const unitType = useSelector(selectAccount)?.display_unit;

  const params = useParams<{ harvestId: string }>();
  const breadcrumbItems = getBreadcrumbMenu('HARVEST_DETAIL', lang, {
    HARVEST_ID: params.harvestId,
  });

  const [harvestData, setHarvestData] = useState<IDataResource>();
  const [lineData, setLineData] = useState<ILineResource>();
  const [asPhotoModalVisible, setAsPhotoModalVisible] = useState(false);
  const [assessPhotos, setAssessPhotos] = useState<string[]>([]);
  const [infoShow, setInfoShow] = useState(false);
  const [infoText, setInfoText] = useState('');
  const [visTrack, setVisTrack] = useState(false);
  const [tagLogs, setTagLogs] = useState<any[]>([]);

  const loadData = useCallback(async () => {
    const res = await sendSingleRequest(
      { require_pdf: true, with_harvest_spats: true, with_history: true },
      'GET',
      `api/farm/line/harvest-data?harvest_id=${params.harvestId}`,
      true,
    );
    if (res.status) {
      const lsp = await sendSingleRequest(
        {},
        'GET',
        `api/farm/line/tag-logs/${res.data.line.id}`,
        true,
      );
      let logs: any[] = [];
      if (lsp.status) {
        for (let x of lsp.data) {
          if (!x.is_on) {
            logs.push(
              {
                id: x.id,
                tag: x.tag,
                is_on: true,
                logged_date: x.created_at,
                user: x.user,
              },
              {
                id: x.id,
                tag: x.tag,
                is_on: false,
                logged_date: x.updated_at,
                user: x.user,
              },
            );
          } else {
            logs.push({
              id: x.id,
              tag: x.tag,
              is_on: true,
              logged_date: x.created_at,
              user: x.user,
            });
          }
        }
      }
      setTagLogs(logs);
      const result: IDataResource = res.data;
      setHarvestData(result);
      const farm = farmsData.find(x => x.id === result.farm.id);
      const line = farm?.lines.find(x => x.id === result.line.id);
      if (line) {
        setLineData(line);
      }
    } else {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'error',
          message: translate(lang, res.data?.message ?? 'Something went wrong'),
        }),
      );
    }
  }, [params.harvestId]);

  const openImages = (images: string[]) => {
    setAsPhotoModalVisible(true);
    setAssessPhotos(images);
  };
  const openInfo = (comment: any) => {
    setInfoShow(true);
    setInfoText(comment && comment.length > 0 ? comment : 'No comments yet');
  };

  const tableColumns = () => {
    if (!harvestData) return [];

    let result = seedColumns(
      harvestData.crop_type,
      unitType,
      harvestData.line.farming_method,
      lang,
    );
    result.splice(1, 0, {
      key: 'line',
      title: translate(lang, 'Farm/Line'),
      render: (x: ISeedingResource) => (
        <span>
          {`${farmsData.find(y => y.id === x.line.farm_id)?.name} - ${
            x.line.line_name
          }`}
        </span>
      ),
    });
    result.push(
      {
        key: 'comment',
        dataIndex: 'comment',
        title: translate(lang, 'Comment'),
        render: (x: any) => (
          <div
            className='btn__modal'
            onKeyDown={() => undefined}
            onClick={openInfo.bind(this, x)}
          >
            {translate(lang, 'View')}
          </div>
        ),
      },
      {
        key: 'images',
        title: translate(lang, 'Photo'),
        render: (x: ISeedingResource) =>
          !!x && x.image_pdf_files && x.image_pdf_files.length > 0 ? (
            <div
              className='btn__modal'
              onClick={openImages.bind(this, x.image_pdf_files)}
            >
              {translate(lang, 'View')}
            </div>
          ) : (
            <span></span>
          ),
      },
      {
        key: 'empty',
        title: '',
        render: (x: any) => <span></span>,
      },
    );
    return result;
  };

  const timelines = useMemo(() => {
    if (!harvestData) {
      return [];
    }
    let planned: any[] = [];
    for (let seed of harvestData.seedings as any) {
      const dates = seed.planned_date_history ?? [];
      for (let i = 0; i < dates.length - 1; i++) {
        if (dates[i] !== dates[i + 1]) {
          planned.push({
            id: `${seed.id} - ${i}`,
            old_date: dates[i],
            new_date: dates[i + 1],
          });
        }
      }
    }
    return [
      ...harvestData.seedings.map(x => ({
        type: 'SEEDING',
        data: x,
      })),
      ...harvestData.assessments.map(x => ({
        type: 'ASSESSMENT',
        data: x,
      })),
      ...harvestData.maintenances.map(x => ({
        type: 'MAINTENANCE',
        data: x,
      })),
      ...harvestData.inventory_seeds.map(x => ({
        type: 'INVENTORY_SEED',
        data: x,
      })),
      ...harvestData.harvests.map(x => ({
        type: 'HARVEST',
        data: x,
      })),
      ...tagLogs.map(x => ({
        type: 'TAG_LOG',
        data: x,
      })),
      ...planned.map((x: any) => ({ type: 'HARVEST_PLANNED', data: x })),
    ];
  }, [harvestData, tagLogs]);

  const lineDetailsMap = useMemo(() => {
    const map = new Map<
      number,
      {
        farmName: string;
        farmNumber: string;
        lineName: string;
        lineType: ILineResource['line_type'];
      }
    >();

    for (const farm of farmsData) {
      for (const line of farm.lines) {
        map.set(line.id, {
          farmName: farm.name,
          farmNumber: farm.farm_number,
          lineName: line.line_name,
          lineType: line.line_type,
        });
      }
    }
    return map;
  }, [farmsData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <div className='harvest-detail-page'>
      <div className='h-calc-80 bg-secondary'>
        <div className='container pos-relative mb-17'>
          <div className='pt-28 pb-28 d-flex justify-between'>
            <BreadcrumbComponent items={breadcrumbItems} />
            <div className='d-flex'>
              {!!harvestData?.pdf_form && (
                <Button
                  size={3}
                  type='fill'
                  width='small'
                  color='blue'
                  onClick={() => formDownload(harvestData.pdf_form)}
                >
                  {translate(lang, '_download_harvest_declaration_form')}
                </Button>
              )}
              {!!harvestData?.has_tracks && (
                <Button
                  className='ml-17'
                  size={3}
                  type='fill'
                  width='small'
                  color='blue'
                  onClick={() => setVisTrack(true)}
                >
                  {translate(lang, '_view_harvest_tracking')}
                </Button>
              )}
            </div>
          </div>
          {harvestData ? (
            <>
              {harvestData.crop_type === 'MUSSEL' ? (
                <MusselHarvestHeader
                  farm={harvestData.farm}
                  line={harvestData.line}
                  harvests={harvestData.harvests as IMusselHarvest[]}
                  harvestID={Number(params.harvestId)}
                  firstStageLength={harvestData.first_stage_length}
                  prevStageLength={harvestData.prev_stage_length}
                  lastStageLength={harvestData.last_stage_length}
                  stageLengthSum={harvestData.stage_length_sum}
                  stageAmountSum={harvestData.stage_amount_sum}
                  grownDays={harvestData.grown_days}
                />
              ) : harvestData.crop_type === 'OYSTER' ? (
                <OysterHarvestHeader
                  farm={harvestData.farm}
                  line={harvestData.line}
                  seedings={harvestData.seedings as IOysterSeeding[]}
                  harvests={harvestData.harvests as IOysterHarvest[]}
                  harvestID={Number(params.harvestId)}
                  stageAmountSum={harvestData.stage_amount_sum}
                  stageMortalitySum={harvestData.stage_mortality_sum}
                />
              ) : (
                <SeaweedHarvestHeader
                  farm={harvestData.farm}
                  line={harvestData.line}
                  seedings={harvestData.seedings as ISeaweedSeeding[]}
                  harvests={harvestData.harvests as ISeaweedHarvest[]}
                />
              )}
              {timelines.length > 0 && (
                <TimelineView dataItems={timelines as any} />
              )}
              {harvestData.combined_lines &&
                harvestData.combined_lines.length > 0 && (
                  <div className='w-100 mt-17'>
                    <div className='white-card pt-28 pr-24 pb-28 pl-24'>
                      <Title
                        size={6}
                        color='black-3'
                        align='left'
                        fontWeight={700}
                        className='mb-24'
                      >
                        {translate(lang, 'Combined Bulk Harvests Information')}
                      </Title>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns:
                            'repeat(auto-fit, minmax(300px, 1fr))',
                          gap: '16px',
                          marginBottom: '24px',
                        }}
                      >
                        {harvestData.combined_lines.map((line, index) => {
                          const lineDetails = lineDetailsMap.get(line.line_id);
                          const {
                            farmName = '',
                            farmNumber = '',
                            lineName = '',
                            lineType = 'LONGLINE' as ILineResource['line_type'],
                          } = lineDetails || {};
                          const lengthLabel =
                            lineType === 'LONGLINE' ? 'distance_m' : undefined;
                          const lengthValue = lengthLabel
                            ? displayValueUnit(
                                line.line_length,
                                unitType,
                                lengthLabel,
                                '-',
                              )
                            : line.line_length;
                          const shellLengthMin = displayValueUnit(
                            line.shell_length,
                            unitType,
                            'size_mm',
                            '-',
                          );
                          const shellLengthMax = line.shell_length_max
                            ? displayValueUnit(
                                line.shell_length_max,
                                unitType,
                                'size_mm',
                                '-',
                              )
                            : null;
                          const shellRange = shellLengthMax
                            ? `${shellLengthMin} ~ ${shellLengthMax}`
                            : shellLengthMin;

                          return (
                            <div
                              key={index}
                              style={{
                                border: '1px solid #e8e8e8',
                                borderRadius: '8px',
                                padding: '16px',
                                backgroundColor: '#fafafa',
                                transition: 'all 0.3s ease',
                              }}
                              onMouseEnter={e => {
                                e.currentTarget.style.boxShadow =
                                  '0 2px 8px rgba(0,0,0,0.1)';
                                e.currentTarget.style.transform =
                                  'translateY(-2px)';
                              }}
                              onMouseLeave={e => {
                                e.currentTarget.style.boxShadow = 'none';
                                e.currentTarget.style.transform =
                                  'translateY(0)';
                              }}
                            >
                              <Subtitle
                                size={4}
                                color='black'
                                align='left'
                                fontWeight={600}
                                className='mb-12'
                              >
                                {farmName} {farmNumber && `(${farmNumber})`}
                              </Subtitle>
                              <div className='d-flex align-center mb-8'>
                                <div style={{ minWidth: '60px' }}>
                                  <Paragrapgh
                                    size={2}
                                    color='black-2'
                                    align='left'
                                    fontWeight={400}
                                    className='mr-8'
                                  >
                                    {translate(lang, 'Line')}:
                                  </Paragrapgh>
                                </div>
                                <Paragrapgh
                                  size={2}
                                  color='black'
                                  align='left'
                                  fontWeight={500}
                                >
                                  {lineName}
                                </Paragrapgh>
                              </div>
                              <div className='d-flex align-center mb-8'>
                                <div style={{ minWidth: '60px' }}>
                                  <Paragrapgh
                                    size={2}
                                    color='black-2'
                                    align='left'
                                    fontWeight={400}
                                    className='mr-8'
                                  >
                                    {translate(
                                      lang,
                                      lineType === 'LONGLINE'
                                        ? 'Length'
                                        : 'Droppers',
                                    )}
                                    :
                                  </Paragrapgh>
                                </div>
                                <Paragrapgh
                                  size={2}
                                  color='black'
                                  align='left'
                                  fontWeight={500}
                                >
                                  {lengthValue}
                                </Paragrapgh>
                              </div>
                              <div className='d-flex align-center'>
                                <div style={{ minWidth: '60px' }}>
                                  <Paragrapgh
                                    size={2}
                                    color='black-2'
                                    align='left'
                                    fontWeight={400}
                                    className='mr-8'
                                  >
                                    {translate(lang, 'Shell Size')}:
                                  </Paragrapgh>
                                </div>
                                <Paragrapgh
                                  size={2}
                                  color='black'
                                  align='left'
                                  fontWeight={500}
                                >
                                  {shellRange}
                                </Paragrapgh>
                              </div>
                              {line.bags_quantity !== null &&
                                line.bags_quantity > 0 && (
                                  <div className='d-flex align-center mt-8'>
                                    <div style={{ minWidth: '60px' }}>
                                      <Paragrapgh
                                        size={2}
                                        color='black-2'
                                        align='left'
                                        fontWeight={400}
                                        className='mr-8'
                                      >
                                        {translate(lang, 'Bags')}:
                                      </Paragrapgh>
                                    </div>
                                    <Paragrapgh
                                      size={2}
                                      color='black'
                                      align='left'
                                      fontWeight={500}
                                    >
                                      {line.bags_quantity}
                                    </Paragrapgh>
                                  </div>
                                )}
                            </div>
                          );
                        })}
                      </div>
                      <div
                        style={{
                          borderTop: '2px solid #1890ff',
                          paddingTop: '20px',
                          marginTop: '8px',
                          backgroundColor: '#f0f7ff',
                          borderRadius: '8px',
                          padding: '20px',
                        }}
                      >
                        <div className='d-flex justify-between align-center mb-12'>
                          <Subtitle
                            size={4}
                            color='black'
                            align='left'
                            fontWeight={600}
                          >
                            {translate(lang, 'Total harvested amount')}:
                          </Subtitle>
                          <Subtitle
                            size={4}
                            color='blue'
                            align='right'
                            fontWeight={700}
                          >
                            {displayValueUnit(
                              harvestData.combined_lines.reduce(
                                (sum, line) => sum + line.amount,
                                0,
                              ),
                              unitType,
                              'weight_kg',
                              '-',
                            )}
                          </Subtitle>
                        </div>
                        <div className='d-flex justify-between align-center'>
                          <Subtitle
                            size={4}
                            color='black'
                            align='left'
                            fontWeight={600}
                          >
                            {translate(lang, 'Total harvested bags')}:
                          </Subtitle>
                          <Subtitle
                            size={4}
                            color='blue'
                            align='right'
                            fontWeight={700}
                          >
                            {harvestData.combined_lines.reduce(
                              (sum, line) => sum + (line.bags_quantity ?? 0),
                              0,
                            )}
                          </Subtitle>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              <div className='w-100 mt-10'>
                <div className='ant-table-wrapper table mt-17'>
                  <Title
                    className='ml-7'
                    size={6}
                    color='black-3'
                    align='default'
                    fontWeight={700}
                  >
                    {translate(lang, 'Seedings')}
                  </Title>
                  <Table
                    rowKey='id'
                    className='table table--isFarm pl-0 pr-0 overflow-auto'
                    pagination={false}
                    columns={tableColumns()}
                    dataSource={harvestData.seedings}
                  />
                </div>
              </div>
              <div className='w-100 mt-10'>
                <MaintenanceTable
                  type={harvestData.crop_type}
                  assessments={harvestData.assessments}
                  maintenances={harvestData.maintenances}
                  floats={harvestData.inventory_seeds}
                  withTitle={true}
                />
              </div>
              {!!lineData && (
                <div className='w-100 mt-10'>
                  <HarvestsTable
                    cropType={harvestData.crop_type}
                    harvests={harvestData.harvests}
                    seedings={harvestData.seedings}
                    onUpdateRow={loadData}
                    farmID={harvestData.farm.id}
                    lineData={lineData}
                  />
                </div>
              )}
              <ModalComponent
                visible={infoShow}
                onCancel={() => setInfoShow(false)}
                type=''
                title={translate(lang, 'Comment')}
                text={infoText}
              />
              {asPhotoModalVisible && (
                <FilesViewModal
                  visible={asPhotoModalVisible}
                  files={assessPhotos}
                  onClose={() => setAsPhotoModalVisible(false)}
                />
              )}
            </>
          ) : (
            <div className='d-flex align-center justify-center'>
              <Spinner />
            </div>
          )}
        </div>
      </div>
      {!!visTrack && !!harvestData?.harvests && (
        <HarvestTrackModal
          visible={visTrack}
          harvests={harvestData.harvests}
          onClose={() => setVisTrack(false)}
        />
      )}
    </div>
  );
};

export default HarvestDetailPage;
